<template>
  <section class="journey-hero">
    <div
      v-if="cover && !coverBroken"
      class="journey-hero-photo"
      :style="{ backgroundImage: `url('${cover}')` }"
    ></div>
    <img v-if="cover" :src="cover" alt="" class="journey-hero-probe" @error="coverBroken = true">
    <div class="journey-hero-shade" aria-hidden="true"></div>

    <button v-if="canEdit" type="button" class="journey-cover-button" @click="$refs.coverPicker.open()">
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path fill="currentColor" d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4zM9 2 7.2 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3.2L15 2H9zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10z"/>
      </svg>
      {{ t('rideInfoReplaceTopImage') }}
    </button>
    <JourneyCoverPicker v-if="canEdit" ref="coverPicker" :ride="ride" :days="days" :cover="cover"/>

    <div class="journey-hero-content">
      <p class="journey-kicker">
        <span class="journey-kicker-line" aria-hidden="true"></span>
        {{ t('posterItinerary') }}
      </p>
      <h1 class="journey-title">{{ ride.title }}</h1>
      <p v-if="dates" class="journey-dates"><bdi>{{ dates }}</bdi></p>

      <ul v-if="stats.days" class="journey-stats">
        <li>
          <strong>{{ stats.days }}</strong>
          <span>{{ stats.days === 1 ? t('posterDay') : t('posterDays') }}</span>
        </li>
        <li v-if="stopCount > 1">
          <strong>{{ stopCount }}</strong>
          <span>{{ t('journeyStops') }}</span>
        </li>
        <li v-if="stats.places">
          <strong>{{ stats.places }}</strong>
          <span>{{ stats.places === 1 ? t('journeyPlace') : t('journeyPlaces') }}</span>
        </li>
      </ul>

      <button v-if="stopCount" type="button" class="journey-explore" @click="$emit('explore')">
        {{ t('journeyExplore') }}
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M12 16.5 5.5 10l1.4-1.4 5.1 5.1 5.1-5.1 1.4 1.4z"/>
        </svg>
      </button>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import { journeyCover, journeyStats } from "@/utils/journeyHighlights";
import JourneyCoverPicker from "./JourneyCoverPicker";

/**
 * The journey design's cover: the trip's photo over the whole width and most
 * of the screen, slowly zooming, with the title, the dates and the trip's
 * numbers (days, stops, places) on frosted chips, and a button down to the
 * stops. Without any photo it is a deep gradient in the theme colour.
 * The dates only where RideDates would show them (shouldPresentTheDates).
 * The ride manager changes the photo from here (JourneyCoverPicker), on
 * their own ride while it is not locked.
 */
export default {
  name: 'JourneyHero',
  mixins: [translationMixin],
  components: { JourneyCoverPicker },
  props: {
    ride: { type: Object, required: true },
    days: { type: Array, default: () => [] },
    stops: { type: Array, default: () => [] },
  },
  data() {
    // Hidden until known, so dates the ride hides never flash in.
    return { coverBroken: false, presentDates: false };
  },
  computed: {
    ...mapGetters(['allRide', 'isUserManager', 'compareToUserId', 'isRideLocked']),
    // The same people who may edit the poster (PosterLayout.canEditPoster).
    canEdit() {
      return this.isUserManager && this.compareToUserId(this.ride.authorId) && !this.isRideLocked;
    },
    cover() {
      return journeyCover(this.ride, this.stops);
    },
    stats() {
      return journeyStats(this.days);
    },
    stopCount() {
      return this.stops.length;
    },
    dates() {
      if (!this.presentDates) return '';
      const { start_date: start, end_date: end } = this.ride;
      if (start && end && start !== end) return `${start} – ${end}`;
      return start || '';
    },
  },
  watch: {
    cover() {
      this.coverBroken = false;
    },
    allRide: {
      handler() {
        this.refreshPresentDates();
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['shouldPresentTheDates']),
    async refreshPresentDates() {
      // shouldPresentTheDates reads allRide.ride: wait for the ride to load.
      if (!this.allRide || !this.allRide.ride) return;
      this.presentDates = await this.shouldPresentTheDates();
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.journey-hero {
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: clamp(480px, 82vh, 780px);
  overflow: hidden;
  color: #fff;
  /* Shown alone when there is no photo. */
  background:
    radial-gradient(120% 90% at 85% 10%, rgba(255, 255, 255, 0.18), transparent 60%),
    var(--primary-background, #0b3d4f);
}

.journey-hero-photo {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transform-origin: 60% 40%;
  animation: journey-drift 26s ease-in-out infinite alternate;
}

.journey-hero-probe {
  display: none;
}

.journey-hero-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(8, 12, 20, 0.35) 0%, rgba(8, 12, 20, 0) 28%),
    linear-gradient(0deg, rgba(8, 12, 20, 0.92) 0%, rgba(8, 12, 20, 0.55) 38%, rgba(8, 12, 20, 0) 72%);
}

.journey-cover-button {
  position: absolute;
  z-index: 2;
  top: 18px;
  inset-inline-end: clamp(16px, 4vw, 32px);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 18px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease;
}

.journey-cover-button:hover,
.journey-cover-button:focus-visible {
  background: rgba(255, 255, 255, 0.3);
}

.journey-hero-content {
  position: relative;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 clamp(20px, 5vw, 56px) clamp(36px, 7vh, 72px);
  animation: journey-rise 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.journey-kicker {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
  font-size: clamp(12px, 2.6vw, 14px);
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
}

/* Spaced-out letters break Hebrew words apart. */
[dir="rtl"] .journey-kicker {
  letter-spacing: 0.04em;
}

.journey-kicker-line {
  width: 36px;
  height: 2px;
  border-radius: 2px;
  background: currentColor;
}

.journey-title {
  max-width: 16ch;
  margin: 0;
  font-size: clamp(38px, 9vw, 92px);
  font-weight: 700;
  line-height: 0.98;
  letter-spacing: -0.025em;
  color: #fff;
  text-shadow: 0 2px 30px rgba(0, 0, 0, 0.35);
  overflow-wrap: anywhere;
}

.journey-dates {
  margin: 16px 0 0;
  font-size: clamp(15px, 3.4vw, 18px);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.journey-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 26px 0 0;
  padding: 0;
  list-style: none;
}

.journey-stats li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.24);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  backdrop-filter: blur(14px) saturate(140%);
}

.journey-stats strong {
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}

.journey-stats span {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
}

.journey-explore {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
  padding: 14px 26px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #0d1117;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 30px -10px rgba(0, 0, 0, 0.5);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.journey-explore:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px -12px rgba(0, 0, 0, 0.6);
}

.journey-explore svg {
  animation: journey-nudge 1.8s ease-in-out infinite;
}

@keyframes journey-drift {
  from { transform: scale(1.04); }
  to { transform: scale(1.16) translate3d(-1.5%, -1%, 0); }
}

@keyframes journey-rise {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
}

@keyframes journey-nudge {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

@media (prefers-reduced-motion: reduce) {
  .journey-hero-photo,
  .journey-hero-content,
  .journey-explore svg {
    animation: none;
  }
}
</style>
